import React, { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Lightbulb,
  Cpu,
  Database,
  QrCode,
  Network,
  Map,
  Globe,
  Rocket,
  Award,
} from "lucide-react";

/* =====================================================================
   BUILDING TOMORROW — elegant horizontal animated roadmap.
   Each milestone is a minimal card with icon + title. A subtle orange
   line drawn at the centerline ties everything together; an extra
   self-drawing progress line at the top fills as the section enters
   view (driven by useScroll/useTransform).
   ===================================================================== */

const MILESTONES = [
  { id: 1, icon: Lightbulb, title: "Engineering Innovation" },
  { id: 2, icon: Cpu,       title: "AI Powered Machine Service Reminder System" },
  { id: 3, icon: Database,  title: "Digital Machine History & Service Records" },
  { id: 4, icon: QrCode,    title: "QR Code Based Machine Identification" },
  { id: 5, icon: Network,   title: "Nationwide Dealer Network" },
  { id: 6, icon: Map,       title: "Expansion Across India" },
  { id: 7, icon: Globe,     title: "International Export Market" },
  { id: 8, icon: Rocket,    title: "Continuous Product Innovation" },
  { id: 9, icon: Award,     title: "World-Class Manufacturing Standards" },
];

function MilestoneCard({ milestone, index, isLast }) {
  const Icon = milestone.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 28 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-60px" }}
      transition={{ duration: 0.55, ease: "easeOut", delay: index * 0.06 }}
      data-testid={`milestone-${milestone.id}`}
      className="relative shrink-0 w-[260px] md:w-[280px] snap-center flex flex-col items-center text-center"
    >
      {/* Step number eyebrow */}
      <div className="font-heading text-[10px] uppercase tracking-[0.32em] text-liftile-orange/85 font-semibold mb-5">
        Phase {String(milestone.id).padStart(2, "0")}
      </div>

      {/* Icon disc */}
      <div className="relative">
        <div
          aria-hidden
          className="absolute inset-0 rounded-full bg-liftile-orange/15 blur-2xl"
        />
        <div className="relative grid place-items-center w-20 h-20 md:w-24 md:h-24 rounded-full border-2 border-liftile-orange/60 bg-liftile-black/40 backdrop-blur-sm">
          <Icon className="w-9 h-9 md:w-10 md:h-10 text-liftile-orange" strokeWidth={1.5} />
        </div>
      </div>

      {/* Connector dot (sits ON the line) */}
      <div className="absolute top-[calc(2.6rem+5rem)] md:top-[calc(2.6rem+6rem)] left-1/2 -translate-x-1/2 w-3 h-3 rounded-full bg-liftile-orange shadow-[0_0_8px_2px_rgba(255,107,26,0.55)] z-10 hidden md:block" />

      {/* Title below */}
      <h3 className="mt-7 md:mt-9 font-heading uppercase text-sm md:text-base text-liftile-cream tracking-tight leading-snug px-2 min-h-[3rem]">
        {milestone.title}
      </h3>

      {/* Right-arrow micro-icon between cards (mobile fallback handled by row gap) */}
      {!isLast && (
        <div
          aria-hidden
          className="hidden lg:block absolute right-[-1.2rem] top-[calc(2.6rem+2rem)] md:top-[calc(2.6rem+2.5rem)] text-liftile-orange/60 font-display text-2xl"
        >
          ›
        </div>
      )}
    </motion.div>
  );
}

export default function BuildingTomorrowSection() {
  const sectionRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start 0.85", "end 0.2"],
  });
  const progressScale = useTransform(scrollYProgress, [0, 1], [0, 1]);

  return (
    <section
      ref={sectionRef}
      data-testid="about-building-tomorrow"
      className="relative bg-liftile-matte-black py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle orange ambient */}
      <div
        aria-hidden
        className="absolute -bottom-32 right-0 w-[500px] h-[500px] rounded-full pointer-events-none opacity-25"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.45) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(50px)",
        }}
      />

      <div className="container-x relative">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.65, ease: "easeOut" }}
          className="max-w-3xl"
        >
          <div className="heading-eyebrow mb-4">Our Future</div>
          <h2
            data-testid="building-tomorrow-heading"
            className="font-display uppercase text-liftile-cream leading-[0.95] tracking-tight text-4xl sm:text-5xl md:text-6xl"
          >
            Building <span className="text-liftile-orange">Tomorrow</span>
          </h2>
          <p className="font-body text-liftile-cream/65 mt-6 text-base md:text-lg leading-relaxed max-w-2xl">
            Our journey has only begun. We continue investing in engineering,
            technology and customer experience to build the next generation of
            construction machinery.
          </p>
        </motion.div>

        {/* Top progress line */}
        <div className="relative mt-14 md:mt-20">
          <div className="relative h-px bg-liftile-cream/10 mb-12 md:mb-16">
            <motion.div
              aria-hidden
              style={{ scaleX: progressScale, transformOrigin: "left" }}
              className="absolute inset-0 h-px bg-liftile-orange"
            />
          </div>

          {/* Center connector line behind cards (desktop) */}
          <div
            aria-hidden
            className="hidden lg:block absolute left-0 right-0 top-[calc(3.2rem+5rem)] h-px bg-gradient-to-r from-transparent via-liftile-orange/35 to-transparent"
          />

          {/* Horizontal rail */}
          <div
            data-testid="milestones-rail"
            className="-mx-4 px-4 overflow-x-auto snap-x snap-mandatory scrollbar-thin"
            style={{ scrollbarColor: "rgba(255,107,26,0.4) transparent" }}
          >
            <div className="flex items-start gap-10 md:gap-14 lg:gap-16 pb-6">
              {MILESTONES.map((m, i) => (
                <MilestoneCard
                  key={m.id}
                  milestone={m}
                  index={i}
                  isLast={i === MILESTONES.length - 1}
                />
              ))}
              <div aria-hidden className="shrink-0 w-2" />
            </div>
          </div>

          <div className="mt-3 flex items-center justify-center md:justify-end gap-2 text-liftile-cream/40 font-heading text-[10px] uppercase tracking-[0.32em]">
            <span>← Drag to explore the roadmap →</span>
          </div>
        </div>
      </div>
    </section>
  );
}
